import { ReactNode } from "react";
import { Alert, Box, CircularProgress, CssBaseline, Typography } from "@mui/material";
import CandBMonogramNavy from "src/icons/CandBMonogramNavy";

export const ADHOC_KYC_NAVY = "#002035";
export const ADHOC_KYC_CORAL = "#FC4C4C";
export const ADHOC_KYC_MINT = "#0BEC9F";

/** Centred column every step renders into (both token and session mode). */
export const AdhocKycCard = ({ children }: { children: ReactNode }): JSX.Element => (
  <Box
    data-testid="adhoc-kyc-card"
    sx={{ width: "100%", maxWidth: 640, mx: "auto", px: 2, py: 3, boxSizing: "border-box" }}>
    {children}
  </Box>
);

/**
 * Token-mode chrome: a slim public header like `/secure-biometrics`, no nav
 * drawer and — deliberately — none of `Layout`'s session hooks
 * (`useHydrateUser`, feature flags), which would 401 an anonymous visitor
 * straight into the logout interceptor.
 */
export const AdhocKycPublicShell = ({ children }: { children: ReactNode }): JSX.Element => (
  <Box data-testid="adhoc-kyc-public-shell" sx={{ minHeight: "100vh", bgcolor: "#F7F8FA" }}>
    <CssBaseline />
    <Box
      component="header"
      sx={{ px: 3, py: 2, bgcolor: "#FFFFFF", borderBottom: "1px solid #E5E7EB" }}>
      <CandBMonogramNavy height={"32"} width={"28"} />
    </Box>
    <AdhocKycCard>{children}</AdhocKycCard>
  </Box>
);

export const AdhocKycHeading = ({ children }: { children: ReactNode }): JSX.Element => (
  <Typography
    component="h1"
    sx={{ fontSize: "24px", fontWeight: 600, color: ADHOC_KYC_NAVY, mb: 1.5 }}>
    {children}
  </Typography>
);

export const AdhocKycBody = ({ children }: { children: ReactNode }): JSX.Element => (
  <Typography sx={{ fontSize: "14px", color: "#1F2123", mb: 2 }}>{children}</Typography>
);

/**
 * Why a customer must type what the card says, not what they usually go by:
 * DataZoo's DVS check compares every element with the issuer's record, so a
 * dropped middle name or a nickname is a hard no-match (prod pilot 2026-10-22).
 */
export const ADHOC_KYC_MATCH_NOTICE =
  "Please ensure all details are exactly as they appear on your driver\u2019s licence or passport, including any middle names, as we check them against the issuing authority\u2019s records.";

/** Shown on every screen where the customer confirms or types identity details. */
export const AdhocKycMatchNotice = (): JSX.Element => (
  <Alert
    severity="info"
    icon={false}
    role="note"
    data-testid="adhoc-kyc-match-notice"
    sx={{ mb: 2, fontSize: "14px", color: ADHOC_KYC_NAVY, bgcolor: "#EEF4FA" }}>
    {ADHOC_KYC_MATCH_NOTICE}
  </Alert>
);

/** Inline error — 4xx bodies are shown here, never turned into a redirect. */
export const AdhocKycError = ({ message }: { message: string }): JSX.Element => (
  <Alert severity="error" role="alert" sx={{ mb: 2 }}>
    {message}
  </Alert>
);

export const AdhocKycSpinner = ({ message }: { message: string }): JSX.Element => (
  <Box role="status" aria-label={message} sx={{ textAlign: "center", py: 6 }}>
    <CircularProgress sx={{ color: ADHOC_KYC_MINT }} />
    <Typography sx={{ mt: 2, color: ADHOC_KYC_NAVY }}>{message}</Typography>
  </Box>
);
